import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Plus, LogOut as LogOutIcon, RotateCcw, Archive as ArchiveIcon, Upload, Sun, Moon, Monitor, Trash2, Power } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";

export function UsersAdminPage() {
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (u) => {
    await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
    load();
  };
  const forceOut = async (u) => {
    if (!window.confirm(`Forzare la disconnessione di ${u.email}?`)) return;
    await api.post(`/users/${u.id}/force-logout`);
    load();
  };

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="users-title">Utenti</h1>
        <button className="ma-btn-primary-sm" onClick={()=>setShowNew(true)} data-testid="btn-new-user"><Plus size={16}/> Nuovo utente</button>
      </div>
      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead><tr><th>Email</th><th>Nome</th><th>Ruolo</th><th>Attivo</th><th>Cambio PWD</th><th>Ultimo accesso</th><th></th></tr></thead>
          <tbody>
            {users.length === 0 && <tr><td colSpan="7" className="ma-empty">Nessun utente.</td></tr>}
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td>{u.email}</td>
                <td>{u.first_name} {u.last_name}</td>
                <td><span className="ma-pill">{u.role}</span></td>
                <td>{u.is_active ? <span className="ma-pill pill-ok">Sì</span> : <span className="ma-pill pill-danger">No</span>}</td>
                <td>{u.must_change_password ? "Richiesto" : "—"}</td>
                <td>{u.last_login_at ? new Date(u.last_login_at).toLocaleString("it-IT") : "—"}</td>
                <td>
                  <button className="ma-btn-link" onClick={()=>toggle(u)} data-testid={`toggle-${u.email}`}>{u.is_active ? "Disattiva" : "Attiva"}</button>
                  {" · "}
                  <button className="ma-btn-link" onClick={()=>forceOut(u)}>Force logout</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewUserModal onClose={()=>setShowNew(false)} onDone={()=>{setShowNew(false); load();}}/>}
    </div>
  );
}

function NewUserModal({ onClose, onDone }) {
  const [f, setF] = useState({ email:"", first_name:"", last_name:"", phone:"", role:"collaborator", password:"", is_active:true });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post("/users", f); onDone(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Errore"); setBusy(false); }
  };
  return (
    <div className="ma-modal-overlay" onClick={onClose}>
      <div className="ma-modal" onClick={e=>e.stopPropagation()}>
        <div className="ma-modal-head">Nuovo utente</div>
        <form onSubmit={submit} className="ma-form-panel">
          <label className="ma-label">Email *</label>
          <input type="email" className="ma-input" required value={f.email} onChange={e=>setF({...f, email:e.target.value})} data-testid="new-user-email"/>
          <div className="ma-row">
            <div style={{flex:1}}><label className="ma-label">Nome *</label>
              <input className="ma-input" required value={f.first_name} onChange={e=>setF({...f, first_name:e.target.value})} data-testid="new-user-first"/></div>
            <div style={{flex:1}}><label className="ma-label">Cognome *</label>
              <input className="ma-input" required value={f.last_name} onChange={e=>setF({...f, last_name:e.target.value})} data-testid="new-user-last"/></div>
          </div>
          <label className="ma-label">Telefono</label>
          <input className="ma-input" value={f.phone} onChange={e=>setF({...f, phone:e.target.value})}/>
          <label className="ma-label">Ruolo</label>
          <select className="ma-input" value={f.role} onChange={e=>setF({...f, role:e.target.value})} data-testid="new-user-role">
            <option value="collaborator">Collaboratore</option><option value="admin">Amministratore</option>
          </select>
          <label className="ma-label">Password iniziale *</label>
          <input type="password" className="ma-input" required value={f.password} onChange={e=>setF({...f, password:e.target.value})} data-testid="new-user-password"/>
          <div className="ma-hint">Min. 12 caratteri con maiuscola, minuscola, numero e carattere speciale. L'utente dovrà cambiarla al primo accesso.</div>
          {err && <div className="ma-error">{err}</div>}
          <div className="ma-form-actions">
            <button type="button" className="ma-btn-secondary" onClick={onClose}>Annulla</button>
            <button type="submit" className="ma-btn-primary" disabled={busy} data-testid="new-user-submit">{busy ? "Creazione…" : "Crea utente"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AuditPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => { api.get("/audit?limit=100").then(r => { setRows(r.data.items); setTotal(r.data.total); }); }, []);
  return (
    <div className="ma-page">
      <h1 className="ma-h1" data-testid="audit-title">Registro attività ({total})</h1>
      <div className="ma-table-wrap">
        <table className="ma-table">
          <thead><tr><th>Data/ora</th><th>Utente</th><th>Azione</th><th>Entità</th><th>ID</th><th>IP</th><th>Dettagli</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="7" className="ma-empty">Nessuna voce nel registro.</td></tr>}
            {rows.map(a => (
              <tr key={a.id}>
                <td>{new Date(a.created_at).toLocaleString("it-IT")}</td>
                <td className="ma-mono">{a.user_id ? a.user_id.slice(0,8) : "—"}</td>
                <td><span className="ma-pill">{a.action}</span></td>
                <td>{a.entity_type || "—"}</td>
                <td className="ma-mono">{a.entity_id ? a.entity_id.slice(0,8) : "—"}</td>
                <td>{a.ip || "—"}</td>
                <td className="ma-mono ma-small">{a.new_values ? JSON.stringify(a.new_values).slice(0, 80) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function ArchivedPage() {
  const [tab, setTab] = useState("subjects");
  const [subjects, setSubjects] = useState([]);
  const [practices, setPractices] = useState([]);
  const [q, setQ] = useState("");

  const loadAll = async () => {
    const [s, p] = await Promise.all([
      api.get("/subjects/archived/list").catch(() => ({ data: [] })),
      api.get("/practices/archived/list").catch(() => ({ data: [] })),
    ]);
    setSubjects(s.data || []);
    setPractices(p.data || []);
  };
  useEffect(() => { loadAll(); }, []);

  const restoreSubject = async (row) => {
    if (!window.confirm(`Ripristinare "${row.display_name}"? Tornerà disponibile con tutte le relazioni e pratiche collegate.`)) return;
    try {
      await api.post(`/subjects/${row.id}/restore`);
      toast.success("Cliente ripristinato.");
      loadAll();
    } catch { /* toast */ }
  };
  const restorePractice = async (row) => {
    if (!window.confirm(`Ripristinare la pratica ${row.code}?`)) return;
    try {
      await api.post(`/practices/${row.id}/restore`);
      toast.success("Pratica ripristinata.");
      loadAll();
    } catch { /* toast */ }
  };

  const flt = (v) => (v || "").toLowerCase().includes(q.toLowerCase());
  const filteredSubjects = subjects.filter(x => !q || flt(x.display_name));
  const filteredPractices = practices.filter(x => !q || flt(x.code) || flt(x.title) || flt(x.subject?.display_name));

  return (
    <div className="ma-page">
      <div className="ma-page-header">
        <h1 className="ma-h1" data-testid="archived-title"><ArchiveIcon size={22} style={{marginRight:8, verticalAlign:"middle"}}/>Archivio</h1>
      </div>
      <p className="ma-sub-desc">Elenco dei record archiviati (soft-delete). Nessun dato è stato cancellato fisicamente; è sempre possibile ripristinarli mantenendo relazioni, pratiche, documenti e storico.</p>
      <div className="ma-tabs">
        <button className={`ma-tab ${tab==="subjects"?"active":""}`} onClick={()=>setTab("subjects")} data-testid="tab-arch-subjects">Clienti archiviati ({subjects.length})</button>
        <button className={`ma-tab ${tab==="practices"?"active":""}`} onClick={()=>setTab("practices")} data-testid="tab-arch-practices">Pratiche archiviate ({practices.length})</button>
      </div>
      <div className="ma-filters">
        <input className="ma-input-sm" placeholder="Cerca…" value={q} onChange={e=>setQ(e.target.value)} data-testid="arch-search"/>
      </div>

      {tab === "subjects" && (
        <div className="ma-table-wrap">
          <table className="ma-table">
            <thead><tr><th>Nome</th><th>Tipo</th><th>Data archiviazione</th><th></th></tr></thead>
            <tbody>
              {filteredSubjects.length === 0 && <tr><td colSpan="4" className="ma-empty">Nessun cliente archiviato.</td></tr>}
              {filteredSubjects.map(row => (
                <tr key={row.id} data-testid={`arch-subj-${row.id}`}>
                  <td><Link to={`/clients/${row.id}`} className="ma-link">{row.display_name}</Link></td>
                  <td><span className="ma-pill">{row.subject_type === "company" ? "Impresa" : "Persona"}</span></td>
                  <td>{row.deleted_at ? new Date(row.deleted_at).toLocaleString("it-IT") : "—"}</td>
                  <td>
                    <button className="ma-btn-link" onClick={()=>restoreSubject(row)} data-testid={`btn-restore-subj-${row.id}`}>
                      <RotateCcw size={12}/> Ripristina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "practices" && (
        <div className="ma-table-wrap">
          <table className="ma-table">
            <thead><tr><th>Codice</th><th>Titolo</th><th>Cliente</th><th>Data archiviazione</th><th></th></tr></thead>
            <tbody>
              {filteredPractices.length === 0 && <tr><td colSpan="5" className="ma-empty">Nessuna pratica archiviata.</td></tr>}
              {filteredPractices.map(row => (
                <tr key={row.id} data-testid={`arch-prac-${row.id}`}>
                  <td className="ma-mono"><Link to={`/practices/${row.id}`} className="ma-link">{row.code}</Link></td>
                  <td>{row.title}</td>
                  <td>{row.subject?.display_name || "—"}</td>
                  <td>{row.deleted_at ? new Date(row.deleted_at).toLocaleString("it-IT") : "—"}</td>
                  <td>
                    <button className="ma-btn-link" onClick={()=>restorePractice(row)} data-testid={`btn-restore-prac-${row.id}`}>
                      <RotateCcw size={12}/> Ripristina
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function SettingsPage() {
  const { mode, setMode } = useTheme();
  const [logo, setLogo] = useState(null);
  const [cfgTable, setCfgTable] = useState("practice_phases");
  const [cfgRows, setCfgRows] = useState([]);
  const [newItem, setNewItem] = useState({ code: "", label: "", name: "", sort_order: 0 });

  const loadLogo = () => api.get("/settings/logo").then(r => setLogo(r.data));
  const loadCfg = (t) => api.get(`/admin/config/${t}`).then(r => setCfgRows(r.data));
  useEffect(() => { loadLogo(); loadCfg(cfgTable); }, [cfgTable]);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo caricato."); loadLogo();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore caricamento logo"); }
  };
  const resetLogo = async () => {
    if (!window.confirm("Ripristinare il logo testuale MasiAdvice?")) return;
    await api.delete("/settings/logo"); toast.success("Logo ripristinato al testuale."); loadLogo();
  };
  const saveItem = async (row) => {
    try { await api.patch(`/admin/config/${cfgTable}/${row.id}`, row); toast.success("Aggiornato."); loadCfg(cfgTable); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Errore"); }
  };
  const addItem = async () => {
    try { await api.post(`/admin/config/${cfgTable}`, newItem);
      toast.success("Aggiunto."); setNewItem({ code: "", label: "", name: "", sort_order: 0 }); loadCfg(cfgTable);
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore"); }
  };
  const delItem = async (row) => {
    if (!window.confirm(`Eliminare "${row.label || row.name || row.code}"? Se in uso verrà bloccato; usa 'Disattiva' per nasconderla.`)) return;
    try { await api.delete(`/admin/config/${cfgTable}/${row.id}`); toast.success("Eliminato."); loadCfg(cfgTable); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Impossibile eliminare"); }
  };
  const runCleanup = async () => {
    if (!window.confirm("Rimuovere i record tecnici di test (pytest/regr_/orph_/src_)? I dati funzionali non verranno toccati.")) return;
    try { const r = await api.post("/admin/cleanup/tech-test-data");
      toast.success("Pulizia completata: " + JSON.stringify(r.data.removed));
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore"); }
  };

  const tables = [
    { key: "practice_phases", label: "Fasi pratica" },
    { key: "practice_statuses", label: "Stati pratica" },
    { key: "practice_outcomes", label: "Esiti pratica" },
    { key: "acquisition_channels", label: "Canali acquisizione" },
    { key: "relationship_types", label: "Tipi relazione" },
    { key: "document_categories", label: "Categorie documenti" },
  ];

  return (
    <div className="ma-page">
      <h1 className="ma-h1" data-testid="settings-title">Impostazioni</h1>

      <div className="ma-panel">
        <div className="ma-panel-head">Tema grafico</div>
        <div className="ma-panel-body" style={{display:"flex", gap:12, flexWrap:"wrap"}}>
          {[{k:"light",l:"Chiaro",I:Sun},{k:"dark",l:"Scuro",I:Moon},{k:"auto",l:"Automatico",I:Monitor}].map(o => (
            <button key={o.k} onClick={() => setMode(o.k)}
                    className={`ma-btn-${mode===o.k?"primary":"secondary"}-sm`}
                    data-testid={`theme-${o.k}`}>
              <o.I size={14}/> {o.l}
            </button>
          ))}
          <div className="ma-hint">La preferenza viene salvata sul tuo utente e sincronizzata tra dispositivi.</div>
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Logo MasiAdvice</div>
        <div className="ma-panel-body">
          {logo?.has_logo ? (
            <>
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/branding/logo?ts=${Date.now()}`}
                    alt="Logo MasiAdvice" style={{maxHeight:80, background:"#fff", padding:8, borderRadius:6}}
                    data-testid="logo-preview"/>
              <div className="ma-sub-xs" style={{marginTop:6}}>
                {logo.original_filename} · {logo.mime_type} · {(logo.size_bytes/1024).toFixed(1)} KB
              </div>
              <div className="ma-form-actions">
                <label className="ma-btn-secondary-sm"><Upload size={14}/> Sostituisci
                  <input type="file" accept=".svg,.png,image/svg+xml,image/png" style={{display:"none"}} onChange={uploadLogo} data-testid="logo-upload"/>
                </label>
                <button className="ma-btn-danger-sm" onClick={resetLogo} data-testid="logo-reset"><Trash2 size={14}/> Ripristina testuale</button>
              </div>
            </>
          ) : (
            <>
              <div className="ma-hint">Nessun logo caricato — viene mostrato il testo MASIADVICE nel rosso istituzionale.</div>
              <label className="ma-btn-primary-sm"><Upload size={14}/> Carica logo (SVG/PNG, max 2 MB)
                <input type="file" accept=".svg,.png,image/svg+xml,image/png" style={{display:"none"}} onChange={uploadLogo} data-testid="logo-upload"/>
              </label>
            </>
          )}
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Configurazioni</div>
        <div className="ma-panel-body">
          <div className="ma-tabs">
            {tables.map(t => (
              <button key={t.key} className={`ma-tab ${cfgTable===t.key?"active":""}`} onClick={() => setCfgTable(t.key)} data-testid={`cfg-tab-${t.key}`}>{t.label}</button>
            ))}
          </div>
          <table className="ma-table">
            <thead><tr><th>Codice</th><th>Etichetta</th><th>Ordine</th><th>Attivo</th><th></th></tr></thead>
            <tbody>
              {cfgRows.length===0 && <tr><td colSpan="5" className="ma-empty">Nessuna voce.</td></tr>}
              {cfgRows.map(r => (
                <tr key={r.id} data-testid={`cfg-row-${r.id}`}>
                  <td><input className="ma-input-sm ma-mono" defaultValue={r.code || ""} onBlur={e=>saveItem({...r, code:e.target.value})}/></td>
                  <td><input className="ma-input-sm" defaultValue={r.label || r.name || ""} onBlur={e=>saveItem({...r, label:e.target.value, name:e.target.value})}/></td>
                  <td><input type="number" className="ma-input-sm" style={{width:70}} defaultValue={r.sort_order||0} onBlur={e=>saveItem({...r, sort_order:parseInt(e.target.value||"0")})}/></td>
                  <td>
                    <button className="ma-btn-link" onClick={()=>saveItem({...r, is_active: !r.is_active})} data-testid={`cfg-toggle-${r.id}`}>
                      <Power size={12}/> {r.is_active?"Attivo":"Disattivato"}
                    </button>
                  </td>
                  <td><button className="ma-btn-link ma-danger" onClick={()=>delItem(r)} data-testid={`cfg-del-${r.id}`}><Trash2 size={12}/></button></td>
                </tr>
              ))}
              <tr>
                <td><input className="ma-input-sm ma-mono" placeholder="codice" value={newItem.code} onChange={e=>setNewItem({...newItem, code:e.target.value})} data-testid="cfg-new-code"/></td>
                <td><input className="ma-input-sm" placeholder="etichetta" value={newItem.label} onChange={e=>setNewItem({...newItem, label:e.target.value, name:e.target.value})} data-testid="cfg-new-label"/></td>
                <td><input type="number" className="ma-input-sm" style={{width:70}} value={newItem.sort_order} onChange={e=>setNewItem({...newItem, sort_order:parseInt(e.target.value||"0")})}/></td>
                <td></td>
                <td><button className="ma-btn-primary-sm" onClick={addItem} disabled={!newItem.code || !newItem.label} data-testid="cfg-add"><Plus size={12}/> Aggiungi</button></td>
              </tr>
            </tbody>
          </table>
          <div className="ma-hint">Le voci in uso non possono essere eliminate. Usa "Attivo/Disattivato" per nasconderle senza perdere lo storico.</div>
        </div>
      </div>

      <div className="ma-panel">
        <div className="ma-panel-head">Pulizia dati tecnici</div>
        <div className="ma-panel-body">
          <p className="ma-hint">Rimuove esclusivamente record e sottoscrizioni tecniche generate dai test automatizzati (prefissi <code>regr_</code>, <code>orph_</code>, <code>src_</code>, <code>pytest</code>, endpoint <code>example.com/push/</code>). Non tocca dati funzionali, documenti reali o backup validi.</p>
          <button className="ma-btn-danger-sm" onClick={runCleanup} data-testid="cleanup-run"><Trash2 size={14}/> Esegui pulizia dati test</button>
        </div>
      </div>
    </div>
  );
}
